/**
 * <s-product-gallery> — sideways-scrolling product media carousel.
 *
 * Native scroll-snap does the swiping; this component only adds previous/next
 * buttons and a progress bar that follows the scroll position.
 *
 * Required markup:
 *   [data-track]    — the horizontally scrolling list of slides.
 *   [data-prev]     — previous button (optional).
 *   [data-next]     — next button (optional).
 *   [data-progress] — progress thumb element whose width/offset tracks the scroll (optional).
 */
class SProductGallery extends HTMLElement {
  #ac;
  #frame = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;

    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    this.progress = this.querySelector('[data-progress]');

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.prevButton?.addEventListener('click', () => this.#step(-1), opts);
    this.nextButton?.addEventListener('click', () => this.#step(1), opts);
    this.track.addEventListener('scroll', this.#scheduleUpdate, { ...opts, passive: true });
    window.addEventListener('resize', this.#scheduleUpdate, { ...opts, passive: true });

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #direction() {
    return getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
  }

  #slideStep() {
    const slide = this.track.firstElementChild;
    if (!slide) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  #step(delta) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({
      left: delta * this.#slideStep() * this.#direction,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const { scrollWidth, clientWidth } = this.track;
    const position = Math.abs(this.track.scrollLeft);
    const maxScroll = Math.max(0, scrollWidth - clientWidth);

    if (this.prevButton) this.prevButton.disabled = position <= 1;
    if (this.nextButton) this.nextButton.disabled = position >= maxScroll - 1;

    if (this.progress && scrollWidth > 0) {
      const visible = clientWidth / scrollWidth;
      const offset = maxScroll > 0 ? (position / maxScroll) * (1 - visible) : 0;
      this.progress.style.width = `${visible * 100}%`;
      this.progress.style.insetInlineStart = `${offset * 100}%`;
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
